import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Avatar,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import type { TFunction } from 'i18next';
import {
  WorkspacePremiumRounded,
  AddRounded,
  PeopleRounded,
  CheckCircleOutlineRounded,
  AccessTimeRounded,
  VisibilityRounded,
} from '@mui/icons-material';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import {
  scholarshipApi,
  type ScholarshipApplicationSnapshotDto,
} from '../../lib/api/scholarship.api';
import { CreateScholarshipDialog } from './components/CreateScholarshipDialog';
import { ReviewApplicationDialog } from './components/ReviewApplicationDialog';
import { ProgramAwardsRoster } from './components/ProgramAwardsRoster';
import { formatDate, formatDateTime } from '../../lib/i18n-format';

interface TermDoc {
  id: string;
  name: string;
  status: string;
  startDate?: string;
}

interface ScholarshipDoc {
  id: string;
  termId: string;
  name: string;
  description: string;
  coveragePercent: number;
  maxApplicants?: number;
  applicationDeadline: string | null;
  isActive: boolean;
}

// ─── Status helpers ───────────────────────────────────────────────────────────

// Stable status ids → chip color. Labels are resolved via t() at render time
// using the matching `scholarshipsPage.status.<id>` key.
const STATUS_COLOR: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  draft: 'default',
  submitted: 'info',
  reviewing: 'warning',
  approved: 'success',
  rejected: 'error',
};

// Term status → chip color / label. Labels reuse the shared `sections.statusLabels.*`
// keys so term status reads the same across the app.
function termStatusColor(status: string): 'success' | 'primary' | 'warning' | 'default' {
  if (status === 'open') return 'success';
  if (status === 'in_progress') return 'primary';
  if (status === 'planned') return 'warning';
  return 'default';
}

function termStatusLabel(status: string, t: TFunction): string {
  const known = ['open', 'in_progress', 'closed', 'planned'];
  return known.includes(status) ? t(`sections.statusLabels.${status}`) : status;
}

function dateLabel(val: string | null): string {
  if (!val) return '—';
  return formatDate(val, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// Returns the formatted deadline, or null when there is no deadline so the
// caller can render the localized "no limit" label via t().
function formatDeadlineDate(val: string | null): string | null {
  if (!val) return null;
  return formatDateTime(val, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Applications sub-panel ───────────────────────────────────────────────────

function ScholarshipApplicationsList({
  tenantId,
  scholarshipId,
  scholarshipCoveragePercent,
}: {
  tenantId: string;
  scholarshipId: string;
  scholarshipCoveragePercent: number;
}) {
  const { t } = useTranslation('sis');
  const [reviewTarget, setReviewTarget] = useState<ScholarshipApplicationSnapshotDto | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['scholarshipApplications', scholarshipId],
    queryFn: () => scholarshipApi.listScholarshipApplications({ tenantId, scholarshipId }),
  });

  if (isLoading)
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={28} />
      </Stack>
    );
  if (error) return <Alert severity="error">{t('scholarshipsPage.applications.loadError')}</Alert>;
  if (!data || data.length === 0) {
    return (
      <Stack alignItems="center" py={6} spacing={1} color="text.disabled">
        <PeopleRounded sx={{ fontSize: 40 }} />
        <Typography variant="body2">{t('scholarshipsPage.applications.empty')}</Typography>
      </Stack>
    );
  }

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{ '& th': { color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem' } }}
            >
              <TableCell>{t('scholarshipsPage.applications.headers.student')}</TableCell>
              <TableCell>{t('scholarshipsPage.applications.headers.status')}</TableCell>
              <TableCell>{t('scholarshipsPage.applications.headers.submitted')}</TableCell>
              <TableCell>{t('scholarshipsPage.applications.headers.coverage')}</TableCell>
              <TableCell align="right">
                {t('scholarshipsPage.applications.headers.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((app) => {
              const chipLabel = STATUS_COLOR[app.status]
                ? t(`scholarshipsPage.status.${app.status}`)
                : app.status;
              const chipColor = STATUS_COLOR[app.status] ?? ('default' as const);
              return (
                <TableRow key={app.id} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar
                        sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: 'primary.main' }}
                      >
                        {app.studentId.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontSize="0.7rem"
                        color="text.secondary"
                      >
                        {app.studentId.slice(0, 10)}…
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip label={chipLabel} color={chipColor} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    {dateLabel(app.submittedAt)}
                  </TableCell>
                  <TableCell>
                    {app.approvedCoveragePercent != null ? (
                      <Chip
                        label={`${app.approvedCoveragePercent}%`}
                        color="success"
                        size="small"
                      />
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('scholarshipsPage.applications.review')}>
                      <IconButton
                        size="small"
                        onClick={() => setReviewTarget(app)}
                        aria-label={t('scholarshipsPage.applications.review')}
                      >
                        <VisibilityRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <ReviewApplicationDialog
        open={Boolean(reviewTarget)}
        onClose={() => setReviewTarget(null)}
        application={reviewTarget}
        tenantId={tenantId}
        scholarshipCoveragePercent={scholarshipCoveragePercent}
        scholarshipId={scholarshipId}
      />
    </>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function ScholarshipsAdminPage() {
  const { t } = useTranslation('sis');
  const { currentTenantId: tenantId } = useTenantStore();
  const [terms, setTerms] = useState<TermDoc[]>([]);
  const [selectedTermId, setSelectedTermId] = useState<string | null>(null);
  const [scholarships, setScholarships] = useState<ScholarshipDoc[]>([]);
  const [loadingTerms, setLoadingTerms] = useState(true);
  const [loadingScholarships, setLoadingScholarships] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // Hide closed terms by default; the count grows every cycle.
  const [showAllTerms, setShowAllTerms] = useState(false);
  const [view, setView] = useState<'applications' | 'awards'>('applications');

  // ── Real-time: terms list ────────────────────────────────────────────────
  useEffect(() => {
    if (!tenantId) return;
    setLoadingTerms(true);
    const unsub = onSnapshot(
      query(collection(db, `tenants/${tenantId}/terms`), orderBy('startDate', 'desc')),
      (snap) => {
        const termList = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<TermDoc, 'id'>),
        })) as TermDoc[];
        setTerms(termList);
        if (!selectedTermId && termList.length > 0) {
          // Prefer the term currently in progress, then an open one, else newest.
          const activeTerm =
            termList.find((t) => t.status === 'in_progress') ??
            termList.find((t) => t.status === 'open');
          setSelectedTermId(activeTerm?.id ?? termList[0]!.id);
        }
        setLoadingTerms(false);
      },
      () => setLoadingTerms(false),
    );
    return unsub;
  }, [tenantId, selectedTermId]);

  // ── Real-time: scholarships for selected term ────────────────────────────
  useEffect(() => {
    if (!tenantId || !selectedTermId) return;
    setLoadingScholarships(true);
    const unsub = onSnapshot(
      query(collection(db, `tenants/${tenantId}/scholarships`), orderBy('createdAt', 'desc')),
      (snap) => {
        const all = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<ScholarshipDoc, 'id'>),
        })) as ScholarshipDoc[];
        setScholarships(all.filter((s) => s.termId === selectedTermId));
        setLoadingScholarships(false);
      },
      () => setLoadingScholarships(false),
    );
    return unsub;
  }, [tenantId, selectedTermId]);

  const selectedTerm = terms.find((t) => t['id'] === selectedTermId);
  const visibleTerms = showAllTerms ? terms : terms.filter((t) => t.status !== 'closed');

  // When switching to "active only", if the current selection is now hidden
  // (a closed term), jump to the first visible term so the dropdown + content
  // stay in sync.
  function handleShowAll(next: boolean) {
    setShowAllTerms(next);
    if (!next && selectedTerm && selectedTerm.status === 'closed') {
      const fallback = terms.find((t) => t.status !== 'closed');
      if (fallback) setSelectedTermId(fallback.id);
    }
  }

  if (!tenantId) return null;

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Header ──────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>
            <WorkspacePremiumRounded />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {t('scholarshipsPage.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('scholarshipsPage.subtitle')}
            </Typography>
          </Box>
        </Stack>
        {view === 'applications' && (
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={() => setCreateOpen(true)}
            disabled={!selectedTermId}
          >
            {t('scholarshipsPage.newScholarship')}
          </Button>
        )}
      </Stack>

      {/* ── View tabs: applications vs fixed program awards ──────────────── */}
      <Tabs
        value={view}
        onChange={(_, v) => setView(v as 'applications' | 'awards')}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="applications" label={t('scholarshipsPage.tabs.applications')} />
        <Tab value="awards" label={t('scholarshipsPage.tabs.awards')} />
      </Tabs>

      {view === 'awards' && <ProgramAwardsRoster />}

      {view === 'applications' && (
        <>
          {/* ── Term selector + status filter ────────────────────────────── */}
          {loadingTerms ? (
            <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />
          ) : terms.length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              {t('scholarshipsPage.noTerms')}
            </Alert>
          ) : (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              mb={3}
            >
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel id="term-select-label">
                  {t('scholarshipsPage.termSelector.label')}
                </InputLabel>
                <Select
                  labelId="term-select-label"
                  label={t('scholarshipsPage.termSelector.label')}
                  value={
                    visibleTerms.some((term) => term.id === selectedTermId) ? selectedTermId : ''
                  }
                  onChange={(e) => setSelectedTermId(e.target.value)}
                >
                  {visibleTerms.map((term) => (
                    <MenuItem key={term.id} value={term.id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{term.name}</span>
                        <Chip
                          label={termStatusLabel(term.status, t)}
                          size="small"
                          color={termStatusColor(term.status)}
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <ToggleButtonGroup
                size="small"
                exclusive
                value={showAllTerms ? 'all' : 'active'}
                onChange={(_, v) => {
                  if (v !== null) handleShowAll(v === 'all');
                }}
              >
                <ToggleButton value="active">
                  {t('scholarshipsPage.termSelector.active')}
                </ToggleButton>
                <ToggleButton value="all">{t('scholarshipsPage.termSelector.all')}</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          )}

          {/* ── Scholarships for selected term ───────────────────────────── */}
          {selectedTermId && (
            <>
              {loadingScholarships ? (
                <Stack alignItems="center" py={8}>
                  <CircularProgress />
                </Stack>
              ) : scholarships.length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    p: 6,
                    textAlign: 'center',
                    borderStyle: 'dashed',
                    borderColor: 'divider',
                  }}
                >
                  <WorkspacePremiumRounded sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
                  <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
                    {t('scholarshipsPage.emptyState.title')}
                  </Typography>
                  <Typography variant="body2" color="text.disabled" mb={2.5}>
                    {t('scholarshipsPage.emptyState.subtitle')}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddRounded />}
                    onClick={() => setCreateOpen(true)}
                  >
                    {t('scholarshipsPage.emptyState.cta')}
                  </Button>
                </Paper>
              ) : (
                <Stack spacing={3}>
                  {scholarships.map((s) => (
                    <Paper
                      key={s['id'] as string}
                      variant="outlined"
                      sx={{ borderRadius: 3, overflow: 'hidden' }}
                    >
                      {/* Scholarship header */}
                      <Box sx={{ px: 3, py: 2, bgcolor: 'action.hover' }}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Stack spacing={0.5}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Typography variant="h6" fontWeight={700}>
                                {s['name'] as string}
                              </Typography>
                              <Chip
                                label={
                                  s['isActive']
                                    ? t('scholarshipsPage.scholarship.active')
                                    : t('scholarshipsPage.scholarship.inactive')
                                }
                                color={s['isActive'] ? 'success' : 'default'}
                                size="small"
                              />
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              {s['description'] as string}
                            </Typography>
                          </Stack>
                          <Stack alignItems="flex-end" spacing={0.5}>
                            <Chip
                              icon={<CheckCircleOutlineRounded fontSize="small" />}
                              label={t('scholarshipsPage.scholarship.coverage', {
                                percent: s['coveragePercent'] as number,
                              })}
                              color="primary"
                              variant="outlined"
                            />
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <AccessTimeRounded
                                fontSize="small"
                                sx={{ color: 'text.disabled', fontSize: 14 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {t('scholarshipsPage.scholarship.deadline', {
                                  date:
                                    formatDeadlineDate(s['applicationDeadline'] as string | null) ??
                                    t('scholarshipsPage.scholarship.noDeadline'),
                                })}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Box>

                      {/* Applications list */}
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={1} px={3} py={1.5}>
                          <PeopleRounded fontSize="small" color="action" />
                          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                            {t('scholarshipsPage.applications.sectionTitle')}
                          </Typography>
                        </Stack>
                        <ScholarshipApplicationsList
                          tenantId={tenantId}
                          scholarshipId={s['id'] as string}
                          scholarshipCoveragePercent={s['coveragePercent'] as number}
                        />
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              )}
            </>
          )}
        </>
      )}

      {/* ── Dialogs ──────────────────────────────────────────────────── */}
      <CreateScholarshipDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        tenantId={tenantId}
        termId={selectedTermId ?? ''}
        termName={(selectedTerm?.['name'] as string) ?? ''}
      />
    </Box>
  );
}
